// frontend/src/pages/PaymentHistory/index.tsx
// Thin orchestrator: state, data wiring, derived values.
// Delegates heavy JSX to sub-components, standard states to shared infra.

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Building2, ArrowLeft } from "lucide-react";

import type { Payment, PaymentSummary } from "./types";
import { fmtMoney, fmtDate, obligationLabel, subLabel } from "./helpers";
import { T, FONT, thStyle } from "./styles";
import { usePayments } from "./usePayments";
import SummaryCard from "./components/SummaryCard";
import PaymentRow from "./components/PaymentRow";
import InfoBanner from "./components/InfoBanner";

import LoadingState from "../../components/common/LoadingState";
import ErrorState from "../../components/common/ErrorState";
import EmptyState from "../../components/common/EmptyState";

export default function PaymentHistory(): React.ReactElement {
  const navigate = useNavigate();
  const [search, setSearch] = useState<string>("");

  const { data: payments = [], isLoading, error, refetch } = usePayments();

  // ── Derived values ──────────────────────────────────────────────────────
  const summary: PaymentSummary = useMemo(function () {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();

    let monthAmt = 0, monthCount = 0;
    let ytdAmt = 0, ytdCount = 0;
    let lastPayment: Payment | null = null;

    for (const p of payments) {
      const d = new Date(p.payment_date);
      if (isNaN(d.getTime())) continue;
      const amt = parseFloat(String(p.amount || 0));

      if (d.getFullYear() === thisYear) {
        ytdAmt += amt;
        ytdCount += 1;
        if (d.getMonth() === thisMonth) {
          monthAmt += amt;
          monthCount += 1;
        }
      }
      if (!lastPayment || new Date(p.payment_date) > new Date(lastPayment.payment_date)) {
        lastPayment = p;
      }
    }

    return { monthAmt, monthCount, ytdAmt, ytdCount, lastPayment };
  }, [payments]);

  const filtered = useMemo(function () {
    if (!search.trim()) return payments;
    const q = search.trim().toLowerCase();
    return payments.filter(function (p) {
      const label = obligationLabel(p).toLowerCase();
      const sub = subLabel(p).toLowerCase();
      const amt = String(p.amount || "").toLowerCase();
      return label.includes(q) || sub.includes(q) || amt.includes(q);
    });
  }, [payments, search]);

  const sorted = useMemo(function () {
    return [...filtered].sort(function (a, b) {
      return new Date(b.payment_date).getTime() - new Date(a.payment_date).getTime();
    });
  }, [filtered]);

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div style={{
      maxWidth: "100%", margin: 0, padding: "28px 32px 90px",
      fontFamily: FONT, color: T.ink,
    }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => navigate(-1)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "transparent", border: 0, padding: "6px 10px 6px 6px",
            font: "inherit", fontSize: 12.5, fontWeight: 600,
            color: T.muted, cursor: "pointer", borderRadius: 6,
            marginBottom: 12, marginLeft: -6,
          }}
        >
          <ArrowLeft size={14} />
          Back
        </button>
        <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: "-0.3px" }}>
          Payment history
        </h1>
        <div style={{ fontSize: 14, color: T.slate, marginTop: 4, fontWeight: 500 }}>
          All payments recorded in Novala
        </div>
      </div>

      {/* Banner */}
      <InfoBanner strong="Payments below reflect what Novala recorded, not confirmed bank settlement.">
        Bank statement reconciliation will add status tracking so you can see which payments actually left your bank.
      </InfoBanner>

      {/* Summary cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        gap: 12,
        marginBottom: 28,
        maxWidth: 720,
      }}>
        <SummaryCard
          label="This month"
          value={fmtMoney(summary.monthAmt)}
          sub={summary.monthCount === 1 ? "1 payment" : summary.monthCount + " payments"}
        />
        <SummaryCard
          label="Year to date"
          value={fmtMoney(summary.ytdAmt)}
          sub={summary.ytdCount === 1 ? "1 payment" : summary.ytdCount + " payments"}
        />
        <SummaryCard
          label="Last payment"
          value={summary.lastPayment ? fmtDate(summary.lastPayment.payment_date) : "-"}
          sub={summary.lastPayment ? obligationLabel(summary.lastPayment) : "No payments yet"}
          smallValue
        />
      </div>

      {/* Search */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <div style={{ position: "relative", flex: 1, maxWidth: 300 }}>
          <Search
            size={14}
            style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: T.muted }}
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by obligation or amount"
            style={{
              width: "100%",
              padding: "8px 14px 8px 32px",
              border: "1px solid " + T.lineDark,
              borderRadius: 8,
              font: "inherit",
              fontSize: 13,
              color: T.ink,
              outline: "none",
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div style={{
        border: "1px solid " + T.lineDark,
        borderRadius: 10,
        overflow: "hidden",
        background: T.card,
      }}>
        {isLoading ? (
          <LoadingState message="Loading payments..." padding={40} fontSize={13} color={T.muted} />
        ) : error ? (
          <div style={{ padding: 16 }}>
            <ErrorState
              message={(error as Error).message || "Failed to load payments"}
              onRetry={() => refetch()}
            />
          </div>
        ) : sorted.length === 0 ? (
          <EmptyState
            icon={
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  background: T.line,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: T.muted,
                }}
              >
                <Building2 size={22} />
              </div>
            }
            title={search ? "No payments match your search" : "No payments yet"}
            description={
              search
                ? "Try a different search term."
                : "When you record a payment, it will show up here."
            }
            color={T.ink}
          />
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, width: 110 }}>Date</th>
                <th style={thStyle}>Obligation</th>
                <th style={{ ...thStyle, width: 160 }}>Method</th>
                <th style={{ ...thStyle, width: 130, textAlign: "right" }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((p, i) => (
                <PaymentRow key={p.id} payment={p} first={i === 0} />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {sorted.length > 0 && (
        <div style={{
          marginTop: 14,
          fontSize: 12,
          color: T.muted,
          fontWeight: 500,
        }}>
          Showing {sorted.length} of {payments.length} payment
          {payments.length === 1 ? "" : "s"}
        </div>
      )}
    </div>
  );
}